import { createFileRoute } from "@tanstack/react-router";
import { DollarSign, Calculator, Receipt, FileText, Users, ShieldCheck } from "lucide-react";
import { SolutionLanding } from "@/components/landing/SolutionLanding";

const URL = "https://busditeads.com/software-de-nomina-para-construccion";
const TITLE = "Software de Nómina para Construcción — Business Dite Ads";
const DESC =
  "Nómina para construcción: pago por hora y por día, integración con control horario, recibos y costo por obra. Prueba gratis 14 días.";

const faqs = [
  {
    q: "¿Soporta pago por hora y por día (jornal)?",
    a: "Sí. Cada empleado puede configurarse como por hora o por día. El pago diario multiplica la tarifa por los días trabajados; el horario multiplica por las horas reales.",
  },
  {
    q: "¿La nómina toma datos del control horario automáticamente?",
    a: "Sí. Los partes aprobados alimentan cada corrida de nómina — no vuelves a tipear horas ni días y el costo de mano de obra se reparte por obra automáticamente.",
  },
  {
    q: "¿Los trabajadores ven su propio recibo?",
    a: "Sí. Cada trabajador tiene una vista privada con el detalle de horas/días, tarifa y bruto.",
  },
  {
    q: "¿Está pensado específicamente para construcción?",
    a: "Sí. Maneja la distribución de mano de obra entre obras, los jornales habituales en construcción y asocia el costo a la rentabilidad de cada proyecto.",
  },
];

export const Route = createFileRoute("/software-de-nomina-para-construccion")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "es_ES" },
    ],
    links: [
      { rel: "canonical", href: URL },
      {
        rel: "alternate",
        hrefLang: "en",
        href: "https://busditeads.com/construction-payroll-software",
      },
      {
        rel: "alternate",
        hrefLang: "pt",
        href: "https://busditeads.com/software-de-folha-de-pagamento-para-construcao",
      },
      { rel: "alternate", hrefLang: "es", href: URL },
      {
        rel: "alternate",
        hrefLang: "x-default",
        href: "https://busditeads.com/construction-payroll-software",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          inLanguage: "es",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SolutionLanding
      eyebrow="Nómina para Construcción"
      h1="Nómina hecha para construcción — por hora, por día, multi-obra"
      subhead="Paga a tu cuadrilla con precisión en cada período. Horas y días vienen del control horario, los costos se reparten por obra y el trabajador recibe recibos digitales."
      primaryCtaLabel="Empezar prueba gratis de 14 días"
      secondaryCtaLabel="Ver precios"
      benefits={[
        "Por hora y por día",
        "Control horario incluido",
        "Costo por obra",
        "Cancela cuando quieras",
      ]}
      featuresHeading="Nómina que entiende cómo paga la construcción"
      features={[
        {
          icon: DollarSign,
          title: "Por hora o por día",
          description:
            "Alterna cada trabajador entre /hora y /día. El sistema calcula el bruto por horas reales o días trabajados.",
        },
        {
          icon: Calculator,
          title: "Costo automático por obra",
          description:
            "Los pagos por día y por hora se reparten proporcionalmente entre las obras en las que trabajó — para rentabilidad real por proyecto.",
        },
        {
          icon: Receipt,
          title: "Recibos digitales",
          description:
            "Genera PDFs con horas/días, tarifa, bruto y período — listos para enviar y descargar.",
        },
        {
          icon: FileText,
          title: "Flujo de aprobación y pago",
          description:
            "Revisa la corrida, confirma cada ítem, genera recibos y cierra el período. Cada cambio queda registrado.",
        },
        {
          icon: Users,
          title: "Empleados y subcontratistas",
          description:
            "Paga empleados en planilla y subcontratistas desde el mismo sistema, con permisos por rol.",
        },
        {
          icon: ShieldCheck,
          title: "Listo para auditoría",
          description:
            "Historial inmutable de nómina, PDFs de recibo y exportaciones para contadores y cierre fiscal.",
        },
      ]}
      howItWorksHeading="De las horas al pago en tres pasos"
      steps={[
        {
          title: "Define la tarifa por trabajador",
          description:
            "Configura a cada uno como por hora o por día con su tarifa. Edita cuando quieras — el historial se mantiene.",
        },
        {
          title: "Trae los partes aprobados",
          description:
            "Inicia una nueva corrida de nómina — las horas aprobadas y los días trabajados se cargan automáticamente.",
        },
        {
          title: "Confirma y emite los recibos",
          description: "Revisa, confirma cada ítem y genera los PDFs listos para enviar.",
        },
      ]}
      socialProofHeading="Por qué los contratistas confían en nuestra nómina"
      socialProof={[
        "Corre la nómina en minutos, no en un día entero",
        "Deja de pagar de más o de menos — cada minuto está registrado",
        "Sabe el costo exacto de mano de obra por obra antes de facturar",
        "Los trabajadores ven su recibo cuando quieran",
        "Soporta jornales habituales en construcción residencial",
        "Disponible en español, portugués e inglés",
      ]}
      faqHeading="Preguntas frecuentes"
      faqs={faqs}
      finalCtaHeading="Deja de temerle al día de pago."
      finalCtaSubhead="Prueba Business Dite Ads gratis 14 días y corre tu próxima nómina en minutos."
    />
  );
}
